import { useState, useEffect } from 'react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { BottomSheet } from '../../components/ui/BottomSheet'
import { Button } from '../../components/ui/Button'
import { paymentSimpleApi } from '../../api/paymentSimple'
import { PAYMENT_METHOD_LABELS, kopecksToRubles } from '../../types/payment_simple'
import type { PaymentMethod } from '../../types/payment_simple'

interface RecordPaymentSheetProps {
  isOpen: boolean
  onClose: () => void
  athleteId: string
  athleteName: string
  groupId: string
}

export function RecordPaymentSheet({
  isOpen,
  onClose,
  athleteId,
  athleteName,
  groupId,
}: RecordPaymentSheetProps) {
  const queryClient = useQueryClient()
  const [amount, setAmount] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash')
  const [description, setDescription] = useState('')
  const [transactionDate, setTransactionDate] = useState(
    new Date().toISOString().split('T')[0]
  )

  // Загружаем настройки оплаты группы
  const { data: paymentConfig } = useQuery({
    queryKey: ['group-payment-config', groupId],
    queryFn: () => paymentSimpleApi.getGroupConfig(groupId),
    enabled: !!groupId && isOpen,
    retry: false,
  })

  // Автоматически подставляем базовую цену при открытии
  useEffect(() => {
    if (isOpen && paymentConfig && !amount) {
      const basePrice = kopecksToRubles(paymentConfig.base_price)
      setAmount(basePrice.toString())
    }
  }, [isOpen, paymentConfig])

  const recordMutation = useMutation({
    mutationFn: (data: {
      group_id: string
      amount: number
      payment_method: PaymentMethod
      description: string
      transaction_date: string
    }) => paymentSimpleApi.recordPayment(athleteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['athlete-balance', athleteId] })
      queryClient.invalidateQueries({ queryKey: ['athlete-transactions', athleteId] })
      queryClient.invalidateQueries({ queryKey: ['group-balances', groupId] })
      handleClose()
    },
  })

  const handleClose = () => {
    setAmount('')
    setPaymentMethod('cash')
    setDescription('')
    setTransactionDate(new Date().toISOString().split('T')[0])
    onClose()
  }

  const handleSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Введите корректную сумму')
      return
    }

    recordMutation.mutate({
      group_id: groupId,
      amount: parseFloat(amount),
      payment_method: paymentMethod,
      description,
      transaction_date: transactionDate,
    })
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-hint)',
    marginBottom: '4px',
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} title="Записать платёж">
      <div className="space-y-4 px-4 pb-4">
        <div>
          <p className="text-sm mb-2" style={{ color: 'var(--text-hint)' }}>
            Спортсмен:
          </p>
          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
            {athleteName}
          </p>
        </div>

        <div>
          <label style={labelStyle}>Сумма (₽) *</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="5000"
            style={inputStyle}
            min="0"
            step="100"
            autoFocus
          />
        </div>

        <div>
          <label style={labelStyle}>Способ оплаты *</label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value as PaymentMethod)}
            style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
          >
            {Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Дата платежа *</label>
          <input
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div>
          <label style={labelStyle}>Комментарий (необязательно)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Например: оплата за февраль"
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
          />
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            onClick={handleSubmit}
            disabled={recordMutation.isPending}
            fullWidth
          >
            {recordMutation.isPending ? 'Сохранение...' : 'Записать платёж'}
          </Button>
          <Button variant="secondary" onClick={handleClose} fullWidth>
            Отмена
          </Button>
        </div>

        {recordMutation.isError && (
          <p className="text-sm text-center" style={{ color: 'var(--danger)' }}>
            {(recordMutation.error as Error)?.message || 'Ошибка при записи платежа'}
          </p>
        )}
      </div>
    </BottomSheet>
  )
}
